// Threat tags under a scored report's title (RESTYLE §2, §4): the YARA rules first (critical tint), then the
// ATT&CK techniques and the technique-less behaviours the tags carry, most severe first (a technique before a
// behaviour at the same severity, then tag order). Each technique appears once, at the severity of its strongest rule.
// Info-level items never show (Windows background traffic trips them on every run), and the row shows only when a
// YARA rule matched or something is medium or above: a benign run has no threat tags (FIG ATT&CK still lists them).
// Nothing is derived live: only scored documents show these.
//   threatItems(tags, yaraRules?, families?) → ThreatTagItem[]   malware families from config extraction first (bold),
//                       then YARA; <ThreatTags> shows about six and folds the rest into "+N"
//   yaraRulesOfTags(tags) → string[]   the rules a signature.yara-match tag's evidence names ("Rule @ target")
import type { SevLevel, ThreatTagItem } from "@/components/ui";
import type { Tag } from "../../types";
import { normTech, shortRule, techniqueHref, techniqueInfo } from "./derive/attack";

const YARA_TAG = "signature.yara-match";
const SEV_WORD = ["info", "low", "medium", "high", "critical"] as const;

const sevOf = (n: unknown): SevLevel => Math.max(1, Math.min(5, Math.round(Number(n) || 1))) as SevLevel;

type Ranked = ThreatTagItem & { sev: SevLevel; order: number; rules: string[] };

export function yaraRulesOfTags(tags: readonly Tag[]): string[] {
  const out: string[] = [];
  for (const t of tags) {
    if (t.id !== YARA_TAG) continue;
    for (const ev of t.evidence ?? []) {
      const m = /^(\S.*?)\s+@\s+\S/.exec(typeof ev === "string" ? ev.trim() : "");
      if (m && !out.some((r) => r.toLowerCase() === m[1].toLowerCase())) out.push(m[1]);
    }
  }
  return out;
}

export function threatItems(tags: readonly Tag[], yaraRules: readonly string[] = [], families: readonly string[] = []): ThreatTagItem[] {
  const fams: ThreatTagItem[] = [];
  const seenFam = new Set<string>();
  for (const raw of families) {
    const family = raw.trim();
    if (!family || seenFam.has(family.toLowerCase())) continue;
    seenFam.add(family.toLowerCase());
    fams.push({ kind: "family", label: family, key: `family:${family.toLowerCase()}`, title: `Malware family ${family} · config extraction` });
  }
  const yara: ThreatTagItem[] = [];
  const seenRules = new Set<string>();
  const addRule = (raw: string) => {
    const rule = raw.trim();
    const k = rule.toLowerCase();
    if (!rule || seenRules.has(k)) return;
    seenRules.add(k);
    yara.push({ kind: "yara", label: rule, key: `yara:${k}`, title: `YARA rule ${rule}` });
  };
  yaraRules.forEach(addRule);
  for (const t of tags) {
    if (t.id !== YARA_TAG) continue;
    for (const ev of t.evidence ?? []) {
      const m = /^(\S.*?)\s+@\s+\S/.exec(typeof ev === "string" ? ev.trim() : "");
      if (m) addRule(m[1]);
    }
  }

  const techs = new Map<string, Ranked>();
  const behaviors: Ranked[] = [];
  tags.forEach((t, order) => {
    if (t.id === YARA_TAG) return;
    const sev = sevOf(t.severity);
    const ids = (t.mitre_ids ?? []).map((m) => (typeof m === "string" ? m.trim() : "")).filter(Boolean);
    if (!ids.length) {
      if (t.title?.trim()) behaviors.push({ kind: "behavior", label: t.title.trim(), sev, order, rules: [shortRule(t.id)], key: `tag:${t.id}:${order}` });
      return;
    }
    for (const raw of ids) {
      const id = normTech(raw);
      const have = techs.get(id);
      if (have) {
        if (sev > have.sev) have.sev = sev;
        if (!have.rules.includes(shortRule(t.id))) have.rules.push(shortRule(t.id));
        continue;
      }
      const name = techniqueInfo(id)?.name;
      techs.set(id, name ? { kind: "technique", id, label: name, href: techniqueHref(id), sev, order, rules: [shortRule(t.id)], key: `t:${id}` } : { kind: "behavior", label: id, href: techniqueHref(id), sev, order, rules: [shortRule(t.id)], key: `t:${id}` });
    }
  });

  const ranked = [...techs.values(), ...behaviors]
    .filter((x) => x.sev > 1)
    .sort((a, b) => b.sev - a.sev || Number(a.kind !== "technique") - Number(b.kind !== "technique") || a.order - b.order);
  if (!fams.length && !yara.length && !ranked.some((x) => x.sev >= 3)) return [];
  return [
    ...fams,
    ...yara,
    ...ranked.map(({ sev, order: _order, rules, ...item }): ThreatTagItem => {
      void _order;
      const what = item.id ? `${item.id} ${item.label}` : item.label;
      return { ...item, sev, title: `${what} · ${SEV_WORD[sev - 1]} · ${rules.join(", ")}` };
    }),
  ];
}
